import { useState, useEffect, useRef } from "react";
import TableSection from "./TableSection";

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const getFilenameFromUrl = (url: string) => {
    if (!url) return "이미지 파일";
    try {
        const decoded = decodeURIComponent(url);
        return decoded.split("/").pop()?.split("?")[0] || "이미지 파일";
    } catch {
        return url.split("/").pop() || "이미지 파일";
    }
};

const IMAGE_RESOLUTION_HINT = "권장: 가로형 1080×380 · 세로형 320×480 · 정사각형 480×480 (1MB 이하)";
const THUMBNAIL_RESOLUTION_HINT = "권장: 64×64px · 고해상도 128×128px, 정사각형 1:1 (1MB 이하)";

interface ContentSection {
    id: number;
    type?: string;
    subtitle: string;
    content?: string;
    tableData?: any;
}

interface DateFields {
    year: string;
    month: string;
    day: string;
}

interface FormData {
    title: string;
    thumbnailImage: File | null;
    thumbnailImagePreview: string;
    popupContent: string;
    contentImage: File | null;
    contentImagePreview: string;
    imageDescription: string;
    isDecorative: boolean;
    contentSections: ContentSection[];
    parentMenu: string;
    startDate: DateFields;
    endDate: DateFields;
}

interface CategoryItem {
    category_code: string;
    name: string;
}

interface PostFormProps {
    formData: FormData;
    onInputChange: (field: string, value: any) => void;
    onImageSelect: (field: string, previewField: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    onAddContentSection: () => void;
    onRemoveContentSection: (id: number) => void;
    onSectionChange: (id: number, field: string, value: any) => void;
    dividerSet: Set<number>;
    onToggleDivider: (index: number) => void;
    onDragStart?: (e: React.DragEvent, index: number) => void;
    onDragOver?: (e: React.DragEvent) => void;
    onDrop?: (e: React.DragEvent, index: number) => void;
    draggedIndex?: number | null;
    onDateChange: (dateType: string, field: string, value: string) => void;
    categoryList: CategoryItem[];
    onSubmit: () => void;
    onCancel: () => void;
    mode?: "create" | "edit" | "view";
    isLoading?: boolean;
    onMoveSection?: (index: number, direction: "up" | "down") => void;
    onEditClick?: () => void;
    onAddTable?: (tableType: string) => void;
    onRemoveTable?: (id: number) => void;
    onTableHeaderChange?: (sectionId: number, target: string, value: string, colIdx?: number) => void;
    onTableCellChange?: (sectionId: number, rIdx: number, cIdx: number, value: any) => void;
    onTableToggleCellType?: (sectionId: number, rIdx: number, cIdx: number) => void;
    onTableAddRow?: (sectionId: number) => void;
    onTableRemoveRow?: (sectionId: number, rIdx: number) => void;
    onTableAddCol?: (sectionId: number) => void;
    onTableRemoveCol?: (sectionId: number) => void;
    onSectionsReorder?: (sections: ContentSection[]) => void;
    onOpenImageModal?: (field: string) => void;
    onRemoveImage?: (field: string, previewField: string) => void;
}

function SortableItem({ id, disabled, children }: { id: number; disabled: boolean; children: (props: { dragHandleProps: any }) => React.ReactNode }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 9999 : undefined,
    };
    return (
        <div ref={setNodeRef} style={style}>
            {children({ dragHandleProps: { ...attributes, ...listeners } })}
        </div>
    );
}

const inputClass = "w-full px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] transition-colors";
const disabledInputClass = "w-full px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] text-[#2a1f1a] bg-[#f5f2f0] cursor-not-allowed";
const labelClass = "block text-[12px] text-gray-400 mb-1 font-medium";
const sectionClass = "bg-white rounded-2xl border border-[#e8e0dc] p-5 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]";
const sectionTitleClass = "text-[15px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-3 mb-4";

export default function PostForm({
    formData,
    onInputChange,
    onImageSelect,
    onAddContentSection,
    onRemoveContentSection,
    onSectionChange,
    dividerSet,
    onToggleDivider,
    onDateChange,
    categoryList,
    onSubmit,
    onCancel,
    mode = "create",
    isLoading = false,
    onMoveSection,
    onEditClick,
    onAddTable,
    onTableHeaderChange,
    onTableCellChange,
    onTableAddRow,
    onTableRemoveRow,
    onTableAddCol,
    onTableRemoveCol,
    onSectionsReorder,
    onOpenImageModal,
    onRemoveImage,
}: PostFormProps) {
    const isViewMode = mode === "view";
    const isEditMode = mode === "edit";
    const title = mode === "create" ? "신규 컨텐츠 등록하기" : mode === "edit" ? "컨텐츠 수정하기" : "컨텐츠 상세보기";

    const textareaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
    const [showTableOptions, setShowTableOptions] = useState(false);
    const tableOptionRef = useRef<HTMLDivElement>(null);
    const [activeId, setActiveId] = useState<number | null>(null);
    const imageDescTextareaRef = useRef<HTMLTextAreaElement>(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    useEffect(() => {
        if (!showTableOptions) return;
        const handler = (e: MouseEvent) => {
            if (tableOptionRef.current && !tableOptionRef.current.contains(e.target as Node)) setShowTableOptions(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showTableOptions]);

    useEffect(() => {
        Object.values(textareaRefs.current).forEach(el => {
            if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; }
        });
    }, [formData.contentSections]);

    useEffect(() => {
        if (imageDescTextareaRef.current) {
            imageDescTextareaRef.current.style.height = "34px";
            imageDescTextareaRef.current.style.height = imageDescTextareaRef.current.scrollHeight + "px";
        }
    }, [formData.imageDescription]);

    const handleDndEnd = (event: any) => {
        setActiveId(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = formData.contentSections.findIndex(s => s.id === active.id);
        const newIndex = formData.contentSections.findIndex(s => s.id === over.id);
        onSectionsReorder?.(arrayMove(formData.contentSections, oldIndex, newIndex));
    };

    return (
        <div className="bg-[#f5f2f0] min-h-screen p-6">
            <div className="max-w-[900px] mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-[22px] font-extrabold text-[#2a1f1a]">{title}</h1>
                    {isViewMode && onEditClick && (
                        <button onClick={onEditClick} className="px-4 py-2 border border-[#887267] text-[#887267] rounded-lg text-[13px] font-medium hover:bg-[#f5f2f0] transition-colors">
                            수정하기
                        </button>
                    )}
                </div>

                {/* 컨텐츠명 */}
                <div className={sectionClass}>
                    <p className={sectionTitleClass}>컨텐츠명 <span className="text-red-400">*</span></p>
                    {isViewMode
                        ? <p className={disabledInputClass}>{formData.title}</p>
                        : <input type="text" value={formData.title} onChange={(e) => onInputChange("title", e.target.value)} placeholder="컨텐츠명 입력" className={inputClass} />
                    }
                </div>

                {/* 썸네일 이미지 */}
                <div className={sectionClass}>
                    <p className={sectionTitleClass}>썸네일 이미지</p>
                    {isViewMode ? (
                        formData.thumbnailImagePreview ? (
                            <div className="flex items-center gap-3">
                                <img src={formData.thumbnailImagePreview} alt="썸네일" className="w-16 h-16 object-cover rounded-lg border border-[#e8e0dc]" />
                                <button onClick={() => window.open(formData.thumbnailImagePreview, "_blank")} className="px-3 py-1.5 border border-[#887267] text-[#887267] rounded-lg text-[12px] hover:bg-[#f5f2f0]">이미지 확인</button>
                                <span className="text-[12px] text-gray-400 truncate max-w-xs">{getFilenameFromUrl(formData.thumbnailImagePreview)}</span>
                            </div>
                        ) : <p className="text-[13px] text-gray-300">없음</p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            <button type="button" onClick={() => onOpenImageModal?.("thumbnailImage")} className="w-fit px-4 py-2 border border-[#887267] text-[#887267] rounded-lg text-[13px] hover:bg-[#f5f2f0] transition-colors">
                                이미지 선택
                            </button>
                            <p className="text-[11px] text-gray-400">{THUMBNAIL_RESOLUTION_HINT}</p>
                            {formData.thumbnailImagePreview && (
                                <div className="flex items-center gap-2">
                                    <img src={formData.thumbnailImagePreview} alt="미리보기" className="w-12 h-12 object-cover rounded-lg border border-[#e8e0dc]" />
                                    <span className="text-[12px] text-[#2a1f1a]">{formData.thumbnailImage?.name || getFilenameFromUrl(formData.thumbnailImagePreview)}</span>
                                    <button onClick={() => onRemoveImage?.("thumbnailImage", "thumbnailImagePreview")} className="w-5 h-5 bg-red-400 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-500">✕</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* 상단 팝업 */}
                <div className={sectionClass}>
                    <p className={sectionTitleClass}>상단 팝업</p>
                    {isViewMode
                        ? <p className={disabledInputClass}>{formData.popupContent || "없음"}</p>
                        : <input type="text" value={formData.popupContent} onChange={(e) => onInputChange("popupContent", e.target.value)} placeholder="상단 팝업 내용 입력" className={inputClass} />
                    }
                </div>

                {/* 컨텐츠 내용 */}
                <div className={sectionClass}>
                    <p className={sectionTitleClass}>컨텐츠 내용 <span className="text-red-400">*</span></p>

                    {/* 대표 이미지 */}
                    <div className="mb-5 pb-4 border-b border-[#f5f2f0]">
                        {isViewMode ? (
                            formData.contentImagePreview ? (
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                        <img src={formData.contentImagePreview} alt="컨텐츠 이미지" className="w-16 h-16 object-cover rounded-lg border border-[#e8e0dc]" />
                                        <button onClick={() => window.open(formData.contentImagePreview, "_blank")} className="px-3 py-1.5 border border-[#887267] text-[#887267] rounded-lg text-[12px] hover:bg-[#f5f2f0]">이미지 확인</button>
                                    </div>
                                    {!formData.isDecorative && formData.imageDescription && (
                                        <p className="text-[12px] text-gray-400">사진설명: {formData.imageDescription}</p>
                                    )}
                                </div>
                            ) : null
                        ) : (
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <button type="button" onClick={() => onOpenImageModal?.("contentImage")} className="px-4 py-2 border border-[#887267] text-[#887267] rounded-lg text-[13px] hover:bg-[#f5f2f0] transition-colors">
                                        대표 이미지 선택
                                    </button>
                                    {formData.contentImagePreview && (
                                        <>
                                            <img src={formData.contentImagePreview} alt="미리보기" className="w-10 h-10 object-cover rounded-lg border border-[#e8e0dc]" />
                                            <button onClick={() => { onRemoveImage?.("contentImage", "contentImagePreview"); onInputChange("imageDescription", ""); onInputChange("isDecorative", false); }}
                                                className="w-5 h-5 bg-red-400 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-500">✕</button>
                                        </>
                                    )}
                                </div>
                                <p className="text-[11px] text-gray-400">{IMAGE_RESOLUTION_HINT}</p>
                                {formData.contentImagePreview && (
                                    <div className="flex items-center gap-3">
                                        <span className="text-[13px] font-medium text-[#2a1f1a]">사진설명</span>
                                        <textarea
                                            ref={imageDescTextareaRef}
                                            value={formData.imageDescription || ""}
                                            onChange={(e) => { onInputChange("imageDescription", e.target.value); e.target.style.height = "34px"; e.target.style.height = e.target.scrollHeight + "px"; }}
                                            placeholder="사진 설명 입력"
                                            rows={1}
                                            className="flex-1 px-3 py-1.5 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267] resize-none overflow-hidden"
                                            style={{ height: "34px" }}
                                        />
                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                            <input type="checkbox" checked={formData.isDecorative}
                                                onChange={(e) => { onInputChange("isDecorative", e.target.checked); if (e.target.checked) onInputChange("imageDescription", ""); }}
                                                className="w-4 h-4 accent-[#887267]" />
                                            <span className="text-[13px] text-[#2a1f1a]">설명 없음</span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* 섹션 목록 */}
                    <DndContext sensors={sensors} collisionDetection={closestCenter}
                        onDragStart={(e) => setActiveId(e.active.id as number)}
                        onDragEnd={handleDndEnd}>
                        <SortableContext items={formData.contentSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                            {formData.contentSections.map((section, index) => (
                                <div key={section.id} className="mb-4">
                                    {index > 0 && (
                                        <div className="relative mb-4">
                                            {isViewMode
                                                ? dividerSet.has(index - 1) && <div className="border-t-2 border-[#887267]"></div>
                                                : <div className={`border-t-2 transition-colors ${dividerSet.has(index - 1) ? "border-[#887267]" : "border-[#e8e0dc]"}`}></div>
                                            }
                                            {!isViewMode && (
                                                <button type="button" onClick={() => onToggleDivider(index - 1)}
                                                    className="absolute right-0 -top-3 px-3 h-6 bg-white rounded border border-[#887267] text-[#887267] text-[11px] font-medium hover:bg-[#f5f2f0] z-10">
                                                    {dividerSet.has(index - 1) ? "구분선 삭제" : "구분선 추가"}
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {section.type === "table" ? (
                                        <SortableItem id={section.id} disabled={isViewMode}>
                                            {({ dragHandleProps }) => (
                                                <TableSection
                                                    section={section}
                                                    isViewMode={isViewMode}
                                                    index={index}
                                                    totalCount={formData.contentSections.length}
                                                    onHeaderChange={onTableHeaderChange!}
                                                    onCellChange={onTableCellChange!}
                                                    onAddRow={onTableAddRow!}
                                                    onRemoveRow={onTableRemoveRow!}
                                                    onAddCol={onTableAddCol!}
                                                    onRemoveCol={onTableRemoveCol!}
                                                    onRemoveSection={onRemoveContentSection}
                                                    onMoveSection={onMoveSection}
                                                    dragHandleProps={dragHandleProps}
                                                />
                                            )}
                                        </SortableItem>
                                    ) : (
                                        <SortableItem id={section.id} disabled={isViewMode}>
                                            {({ dragHandleProps }) => (
                                                <div className="flex items-start gap-2">
                                                    <div className="flex-1 flex gap-2 flex-wrap items-start">
                                                        <input type="text" value={section.subtitle}
                                                            onChange={(e) => onSectionChange(section.id, "subtitle", e.target.value)}
                                                            placeholder="소제목 입력" disabled={isViewMode}
                                                            className={`w-32 h-9 px-3 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267] ${isViewMode ? "bg-[#f5f2f0] cursor-not-allowed" : "bg-white"}`}
                                                        />
                                                        <textarea
                                                            ref={el => { textareaRefs.current[section.id] = el; }}
                                                            value={section.content}
                                                            onChange={(e) => { onSectionChange(section.id, "content", e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
                                                            placeholder="내용 입력" disabled={isViewMode} rows={1}
                                                            className={`flex-1 min-w-[120px] px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267] resize-none overflow-hidden ${isViewMode ? "bg-[#f5f2f0] cursor-not-allowed" : "bg-white"}`}
                                                        />
                                                        {index > 0 && !isViewMode && (
                                                            <button type="button" onClick={() => onRemoveContentSection(section.id)}
                                                                className="w-8 h-8 rounded-full bg-[#887267] flex items-center justify-center hover:bg-[#6b5248] transition-colors shrink-0">
                                                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                                            </button>
                                                        )}
                                                        {!isViewMode && (
                                                            <div {...dragHandleProps} className="flex flex-col gap-0.5 p-2 cursor-grab active:cursor-grabbing text-[#b8a39b]">
                                                                {[1,2,3].map(i => <div key={i} className="w-4 h-0.5 bg-current"></div>)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </SortableItem>
                                    )}
                                </div>
                            ))}
                        </SortableContext>
                        <DragOverlay>{null}</DragOverlay>
                    </DndContext>

                    {/* 하단 추가 버튼 */}
                    {!isViewMode && (
                        <div className="flex justify-center gap-2 mt-4 flex-wrap">
                            <button onClick={onAddContentSection}
                                className="px-4 py-2 border border-[#887267] text-[#887267] rounded-lg text-[13px] font-medium hover:bg-[#f5f2f0] transition-colors flex items-center gap-1.5">
                                <svg className="w-3 h-3" viewBox="0 0 10 10" fill="currentColor"><path d="M10 6H6V10H4V6H0V4H4V0H6V4H10V6Z" /></svg>
                                내용 추가
                            </button>
                            <div className="relative" ref={tableOptionRef}>
                                <button type="button" onClick={() => setShowTableOptions(v => !v)}
                                    className={`px-4 py-2 border rounded-lg text-[13px] font-medium transition-colors flex items-center gap-1.5 ${showTableOptions ? "bg-[#887267] border-[#887267] text-white" : "border-[#887267] text-[#887267] hover:bg-[#f5f2f0]"}`}>
                                    <svg className="w-3 h-3" viewBox="0 0 10 10" fill="currentColor"><path d="M10 6H6V10H4V6H0V4H4V0H6V4H10V6Z" /></svg>
                                    표 추가
                                </button>
                                {showTableOptions && (
                                    <div className="absolute bottom-11 left-1/2 -translate-x-1/2 z-20 bg-white border border-[#e8e0dc] rounded-xl shadow-lg p-3 flex gap-2 whitespace-nowrap">
                                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-r border-b border-[#e8e0dc] rotate-45" />
                                        <button type="button" onClick={() => { onAddTable?.("check"); setShowTableOptions(false); }}
                                            className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg border border-[#e8e0dc] hover:border-[#887267] hover:bg-[#f5f2f0] transition-colors">
                                            <span className="text-[12px] font-semibold text-[#2a1f1a]">체크용</span>
                                            <span className="text-[10px] text-gray-400">최대 6열</span>
                                        </button>
                                        <button type="button" onClick={() => { onAddTable?.("text"); setShowTableOptions(false); }}
                                            className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg border border-[#e8e0dc] hover:border-[#887267] hover:bg-[#f5f2f0] transition-colors">
                                            <span className="text-[12px] font-semibold text-[#2a1f1a]">텍스트용</span>
                                            <span className="text-[10px] text-gray-400">최대 4열</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* 상위 메뉴 */}
                <div className={sectionClass}>
                    <p className={sectionTitleClass}>상위 메뉴 <span className="text-red-400">*</span></p>
                    {isViewMode ? (
                        <p className={disabledInputClass}>{categoryList.find(c => c.category_code === formData.parentMenu)?.name || formData.parentMenu}</p>
                    ) : (
                        <select value={formData.parentMenu} onChange={(e) => onInputChange("parentMenu", e.target.value)}
                            className="w-full px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] bg-white">
                            <option value="">상위 메뉴 선택</option>
                            {categoryList.map(cat => <option key={cat.category_code} value={cat.category_code}>{cat.name}</option>)}
                        </select>
                    )}
                </div>

                {/* 노출기간 */}
                <div className={sectionClass}>
                    <p className={sectionTitleClass}>노출기간</p>
                    <div className="flex items-center gap-2 flex-wrap">
                        {(["startDate", "endDate"] as const).map((dateType, di) => (
                            <>
                                {di === 1 && <span className="text-[#887267] font-bold mx-2">~</span>}
                                {(["year", "month", "day"] as const).map((field, fi) => (
                                    <div key={field} className="flex items-center gap-1">
                                        <input type="text" value={formData[dateType][field]}
                                            onChange={(e) => onDateChange(dateType, field, e.target.value)}
                                            maxLength={field === "year" ? 4 : 2}
                                            disabled={isViewMode}
                                            className={`${field === "year" ? "w-16" : "w-10"} h-9 px-2 border border-[#e8e0dc] rounded-lg text-[13px] text-center outline-none focus:border-[#887267] ${isViewMode ? "bg-[#f5f2f0] cursor-not-allowed" : "bg-white"}`}
                                        />
                                        <span className="text-[13px] text-[#2a1f1a]">{["년", "월", "일"][fi]}</span>
                                    </div>
                                ))}
                            </>
                        ))}
                    </div>
                </div>

                {/* 버튼 */}
                <div className="flex gap-3 mt-2">
                    <button onClick={onCancel}
                        className="flex-1 h-12 border border-[#887267] text-[#887267] rounded-xl text-[15px] font-bold hover:bg-[#f5f2f0] transition-colors">
                        {isViewMode ? "닫기" : "취소"}
                    </button>
                    {!isViewMode && (
                        <button onClick={onSubmit} disabled={isLoading}
                            className={`flex-1 h-12 text-white rounded-xl text-[15px] font-bold transition-colors ${isLoading ? "bg-[#b8a39b] cursor-not-allowed" : "bg-[#887267] hover:bg-[#6b5248]"}`}>
                            {isLoading ? "처리 중..." : isEditMode ? "수정하기" : "등록하기"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}